import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  Link,
  useSearchParams,
} from "react-router-dom";

import { AdminShell } from "@/pages/admin/AdminShell";
import { InternalNoteSection } from "@/pages/admin/InternalNoteSection";
import { ReviewActions } from "@/pages/admin/ReviewActions";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  getAdminAccessRequest,
  listAdminAccessRequests,
  type AdminAccessRequest,
  type AdminAccessRequestSummary,
} from "@/lib/api/admin-access-requests";
import { formatAdminAccessScopeLabel } from "@/lib/api/access-request-form";
import { copyTextToClipboard } from "@/lib/clipboard";
import {
  ADMIN_REQUESTS_PAGE_SIZE,
  adminResultRangeLabel,
  adminSortColumnLabel,
  paginateAdminAccessRequests,
  sortAdminAccessRequests,
  type AdminRequestSortDir,
  type AdminRequestSortKey,
} from "@/lib/api/admin-access-request-list";
import {
  environmentToLabel,
  environmentToListLabel,
  formatReviewedMeta,
  formatSubmittedDate,
  integrationLabel,
  labelToStatus,
  previewIntendedUseCase,
  requesterTypeLabel,
  statusToLabel,
  volumeToLabel,
  type AdminStatusLabel,
} from "@/lib/api/admin-access-request-labels";
import type { RequesterType } from "@/lib/api/access-requests";
import {
  getAdminAuthSessionSnapshot,
  subscribeAdminAuthSession,
} from "@/lib/auth/admin-session";

import "@/styles/admin-panel.css";

/** Landing / “clear filters” home: triage pending work first. */
const DEFAULT_STATUS_FILTER: AdminStatusLabel = "In review";
const DRAWER_CLOSE_MS = 220;

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "refreshing" }
  | { kind: "ready" }
  | { kind: "error"; message: string };

type StatusFilterKey = "all" | "review" | "approved" | "denied";

function statusFilterFromKey(key: StatusFilterKey): AdminStatusLabel | null {
  if (key === "review") return "In review";
  if (key === "approved") return "Approved";
  if (key === "denied") return "Denied";
  return null;
}

const EMPTY_SUMMARY: AdminAccessRequestSummary = {
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
};

const SORTABLE_COLUMNS: AdminRequestSortKey[] = [
  "applicant",
  "business",
  "type",
  "environment",
  "volume",
  "status",
  "submitted",
];

function TypePill({ type }: { type: RequesterType }) {
  const label = requesterTypeLabel(type);
  const isPartner = type === "partner";
  return (
    <span className={`type-pill ${isPartner ? "type-partner" : "type-customer"}`}>
      <i
        className={`ti ${isPartner ? "ti-building-skyscraper" : "ti-building"}`}
        aria-hidden="true"
      />
      {label}
    </span>
  );
}

function EnvironmentPill({
  environment,
}: {
  environment: AdminAccessRequest["environment"];
}) {
  const label = environmentToListLabel(environment);
  const cls =
    environment === "live"
      ? "env-live"
      : environment === "sandbox"
        ? "env-sandbox"
        : "env-legacy";
  return (
    <span className={`env-pill ${cls}`} data-testid="admin-list-environment">
      {label}
    </span>
  );
}

function StatusBadge({ status }: { status: AdminAccessRequest["status"] }) {
  const label = statusToLabel(status);
  const cls =
    label === "Approved"
      ? "s-approved"
      : label === "Denied"
        ? "s-denied"
        : "s-review";
  return (
    <span className={`status-badge ${cls}`}>
      <span className="s-dot" />
      {label}
    </span>
  );
}

function CopyValueButton({
  value,
  label,
  testId,
  onCopied,
}: {
  value: string;
  label: string;
  testId?: string;
  onCopied: (message: string) => void;
}) {
  return (
    <button
      type="button"
      className="copy-value-btn"
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation();
        void copyTextToClipboard(value)
          .then(() => onCopied(`Copied ${label}`))
          .catch(() => onCopied("Could not copy to clipboard."));
      }}
    >
      <i className="ti ti-copy" aria-hidden="true" />
    </button>
  );
}

function RequestDetailBody({
  request,
  onRequestUpdated,
  onReviewed,
  onCopied,
}: {
  request: AdminAccessRequest;
  onRequestUpdated: (request: AdminAccessRequest) => void;
  onReviewed: (request: AdminAccessRequest) => void;
  onCopied: (message: string) => void;
}) {
  const reviewedMeta = formatReviewedMeta(
    request.reviewed_at,
    request.reviewed_by_name,
  );
  const isSandbox = request.environment === "sandbox";
  const locationIds = request.location_ids ?? [];
  const locationNames = request.location_names ?? [];
  const namespaces = request.namespaces ?? [];
  // Locations carries the empty state for a locations scope; Location IDs is the
  // legacy field and only renders when the request actually has IDs.
  const showLocationNames =
    locationNames.length > 0 ||
    (!isSandbox &&
      request.access_scope === "locations" &&
      locationIds.length === 0);
  const showLocationIds = locationIds.length > 0;
  const showNamespaces =
    namespaces.length > 0 ||
    (!isSandbox && request.access_scope === "namespaces");
  // The API returns demo locations for the whole applicant account. Only show
  // locations provisioned for this request/integrator in its detail drawer.
  const demoLocations = (request.test_locations ?? []).filter(
    (location) => location.on_this_request,
  );
  const demoLocationNames = demoLocations.map(
    (location) => location.display_name,
  );
  const showDemoLocations = isSandbox || demoLocations.length > 0;

  return (
    <>
      <div className="detail-grid">
        <div className="dfield">
          <div className="dlabel">Applicant</div>
          <div className="dval">{request.contact_name}</div>
          <div className="dval muted dval--with-copy">
            <span>{request.email}</span>
            <CopyValueButton
              value={request.email}
              label="email"
              testId="admin-copy-email"
              onCopied={onCopied}
            />
          </div>
        </div>
        <div className="dfield">
          <div className="dlabel">Business</div>
          <div className="dval">{request.company_name}</div>
        </div>
      </div>

      <hr className="divider" />

      <div className="dfield full">
        <div className="dlabel">Integrator</div>
        <div className="dval" data-testid="admin-detail-integrator-name">
          {request.integrator_name?.trim()
            ? request.integrator_name
            : "Not provided"}
        </div>
      </div>

      <hr className="divider" />

      <div className="dfield full">
        <div className="dlabel">Request ID</div>
        <div className="dval dval--with-copy">
          <code className="request-id-code">{request.id}</code>
          <CopyValueButton
            value={request.id}
            label="request ID"
            testId="admin-copy-request-id"
            onCopied={onCopied}
          />
        </div>
      </div>

      <hr className="divider" />

      <div className="dfield use-case">
        <div className="dlabel">Intended use case</div>
        <div className="dval use-case-text" data-testid="admin-detail-use-case">
          {request.intended_use_case}
        </div>
      </div>

      <hr className="divider" />

      <div className="detail-grid">
        <div className="dfield">
          <div className="dlabel">Environment</div>
          <div className="dval" data-testid="admin-detail-environment">
            {environmentToLabel(request.environment)}
          </div>
        </div>
        <div className="dfield">
          <div className="dlabel">Access scope</div>
          <div className="dval" data-testid="admin-detail-access-scope">
            {formatAdminAccessScopeLabel(
              request.environment,
              request.access_scope,
            )}
          </div>
        </div>
      </div>

      {showLocationNames ? (
        <>
          <hr className="divider" />
          <div className="dfield">
            <div className="dlabel dlabel--with-copy">
              <span>Locations</span>
              {locationNames.length > 0 ? (
                <CopyValueButton
                  value={locationNames.join(", ")}
                  label="locations"
                  testId="admin-copy-location-names"
                  onCopied={onCopied}
                />
              ) : null}
            </div>
            <div className="dval" data-testid="admin-detail-location-names">
              {locationNames.map((locationName) => (
                <span className="branch-chip" key={locationName}>
                  {locationName}
                </span>
              ))}
              {locationNames.length === 0 ? (
                <span className="dval muted">None</span>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      {showLocationIds ? (
        <>
          <hr className="divider" />
          <div className="dfield">
            <div className="dlabel dlabel--with-copy">
              <span>Location IDs</span>
              <CopyValueButton
                value={locationIds.join(", ")}
                label="location IDs"
                testId="admin-copy-location-ids"
                onCopied={onCopied}
              />
            </div>
            <div className="dval">
              {locationIds.map((locationId) => (
                <span className="branch-chip" key={locationId}>
                  {locationId}
                </span>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {showNamespaces ? (
        <>
          <hr className="divider" />
          <div className="dfield">
            <div className="dlabel dlabel--with-copy">
              <span>Namespaces</span>
              {namespaces.length > 0 ? (
                <CopyValueButton
                  value={namespaces.join(", ")}
                  label="namespaces"
                  testId="admin-copy-namespaces"
                  onCopied={onCopied}
                />
              ) : null}
            </div>
            <div className="dval">
              {namespaces.map((ns) => (
                <span className="ns-chip" key={ns}>
                  {ns}
                </span>
              ))}
              {namespaces.length === 0 ? (
                <span className="dval muted">None</span>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      <hr className="divider" />

      <div className="detail-grid">
        <div className="dfield">
          <div className="dlabel">Expected daily volume</div>
          <div className="dval" data-testid="admin-detail-volume">
            {volumeToLabel(request.expected_daily_volume)}
          </div>
        </div>
        <div className="dfield">
          <div className="dlabel">Requested integrations</div>
          <div className="dval">
            {(request.integrations ?? []).map((integration) => (
              <span className="integ-chip" key={integration}>
                <i
                  className={`ti ${integration.includes("zapier") ? "ti-bolt" : "ti-code"}`}
                  aria-hidden="true"
                />
                {integrationLabel(integration)}
              </span>
            ))}
          </div>
        </div>
        {reviewedMeta ? (
          <div className="dfield full">
            <div className="dlabel">Reviewed</div>
            <div className="dval muted">{reviewedMeta}</div>
          </div>
        ) : null}
      </div>

      {showDemoLocations ? (
        <>
          <hr className="divider" />
          <div className="dfield">
            <div className="dlabel dlabel--with-copy">
              <span>Demo locations</span>
              {demoLocationNames.length > 0 ? (
                <CopyValueButton
                  value={demoLocationNames.join(", ")}
                  label="demo locations"
                  testId="admin-copy-demo-locations"
                  onCopied={onCopied}
                />
              ) : null}
            </div>
            <div className="dval" data-testid="admin-detail-demo-locations">
              {demoLocations.length === 0 ? (
                <span className="dval muted">None</span>
              ) : (
                demoLocations.map((location) => (
                  <span
                    className="branch-chip"
                    data-testid={`admin-demo-location-${location.id}`}
                    key={location.id}
                  >
                    {location.display_name}
                  </span>
                ))
              )}
            </div>
          </div>
        </>
      ) : null}

      <hr className="divider" />

      <InternalNoteSection
        key={`${request.id}:${request.internal_note ?? ""}:${request.updated_at}`}
        request={request}
        onUpdated={onRequestUpdated}
      />

      <ReviewActions request={request} onReviewed={onReviewed} />
    </>
  );
}

export default function AdminHomePage() {
  usePageTitle("API Admin Panel — Glofox");
  const [searchParams, setSearchParams] = useSearchParams();
  const session = useSyncExternalStore(
    subscribeAdminAuthSession,
    getAdminAuthSessionSnapshot,
    getAdminAuthSessionSnapshot,
  );
  const selectedRequestId = searchParams.get("request");
  const urlSearchQ = searchParams.get("q")?.trim() ?? "";

  const [statusFilter, setStatusFilter] = useState<AdminStatusLabel | null>(
    () => (urlSearchQ ? null : DEFAULT_STATUS_FILTER),
  );
  const [typeFilter, setTypeFilter] = useState<"all" | RequesterType>("all");
  const [search, setSearch] = useState(urlSearchQ);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearchQ);
  const [prevUrlSearchQ, setPrevUrlSearchQ] = useState(urlSearchQ);
  const [requests, setRequests] = useState<AdminAccessRequest[]>([]);
  const [summary, setSummary] =
    useState<AdminAccessRequestSummary>(EMPTY_SUMMARY);
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [sortKey, setSortKey] = useState<AdminRequestSortKey>("submitted");
  const [sortDir, setSortDir] = useState<AdminRequestSortDir>("desc");
  const [page, setPage] = useState(1);
  const [detailRequest, setDetailRequest] = useState<AdminAccessRequest | null>(
    null,
  );
  const [detailError, setDetailError] = useState<{
    requestId: string;
    message: string;
  } | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [closingRequestId, setClosingRequestId] = useState<string | null>(null);
  // When paginating with a drawer open, prefer `page` over selectionPage so the
  // table advances immediately while the close animation still runs.
  const [preferExplicitPage, setPreferExplicitPage] = useState(false);
  const drawerRef = useRef<HTMLElement | null>(null);
  const drawerCloseRef = useRef<HTMLButtonElement | null>(null);
  const drawerCloseTimeoutRef = useRef<number | null>(null);
  // Last open request id — used to restore row focus after any close path
  // (requestClose, browser Back, or other URL drops of `request`).
  const drawerFocusRowIdRef = useRef<string | null>(null);

  // Hydrate when `q` changes from outside (deep link / back-forward / sidebar).
  // Skip when URL is only catching up to local debounce — otherwise typing
  // would force All statuses and clearing the box would wipe Approved/Denied.
  if (urlSearchQ !== prevUrlSearchQ) {
    setPrevUrlSearchQ(urlSearchQ);
    if (urlSearchQ !== debouncedSearch) {
      setSearch(urlSearchQ);
      setDebouncedSearch(urlSearchQ);
      if (urlSearchQ) {
        setStatusFilter(null);
      } else {
        setStatusFilter(DEFAULT_STATUS_FILTER);
      }
    }
  }

  // Drop close/pagination overrides only after `request` is gone from the URL.
  // Clearing them in the same turn as setSearchParams races RR7's startTransition:
  // pageForView can snap to selectionPage, and the drawer can drop --closing
  // and re-run the enter animation before unmount.
  if (selectedRequestId === null) {
    if (preferExplicitPage) {
      setPreferExplicitPage(false);
    }
    if (closingRequestId !== null) {
      setClosingRequestId(null);
    }
  }

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 250);
    return () => window.clearTimeout(handle);
  }, [search]);

  // Keep the URL `q` in sync with the debounced search (preserve `request`).
  useEffect(() => {
    if (!session) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const current = next.get("q")?.trim() ?? "";
        if (debouncedSearch) {
          if (current === debouncedSearch) {
            return prev;
          }
          next.set("q", debouncedSearch);
        } else if (current) {
          next.delete("q");
        } else {
          return prev;
        }
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, session, setSearchParams]);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;

    async function load() {
      // Yield so loading UI updates are not synchronous setState inside the effect.
      await Promise.resolve();
      if (cancelled) {
        return;
      }

      setLoadState((prev) =>
        prev.kind === "ready" || prev.kind === "refreshing"
          ? { kind: "refreshing" }
          : { kind: "loading" },
      );
      setPage(1);

      try {
        const response = await listAdminAccessRequests({
          status: labelToStatus(statusFilter),
          requester_type: typeFilter === "all" ? undefined : typeFilter,
          q: debouncedSearch || undefined,
        });
        if (cancelled) {
          return;
        }
        setRequests(response.requests);
        setSummary(response.summary);
        setLoadState({ kind: "ready" });
      } catch {
        if (cancelled) {
          return;
        }
        setRequests([]);
        setSummary(EMPTY_SUMMARY);
        setLoadState({
          kind: "error",
          message: "We couldn't load access requests. Please try again.",
        });
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, reloadKey, session, statusFilter, typeFilter]);

  const detailFromList = selectedRequestId
    ? (requests.find((row) => row.id === selectedRequestId) ?? null)
    : null;

  useEffect(() => {
    if (!session || !selectedRequestId) {
      return;
    }

    const requestId = selectedRequestId;

    // Always fetch detail so internal-note fields (omitted from list) load.
    // List rows can still paint optimistically via detailFromList.
    let cancelled = false;

    async function loadDetail() {
      await Promise.resolve();
      if (cancelled) {
        return;
      }

      try {
        const detail = await getAdminAccessRequest(requestId);
        if (!cancelled) {
          setDetailRequest(detail);
          setDetailError(null);
        }
      } catch {
        if (!cancelled) {
          // Keep list fallback visible when present; only error for deep links.
          if (!detailFromList) {
            setDetailRequest(null);
            setDetailError({
              requestId,
              message: "We couldn't load that request.",
            });
          }
        }
      }
    }

    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [detailFromList, selectedRequestId, session]);

  const activeSidebar = useMemo(() => {
    if (statusFilter === "In review") return "review";
    if (statusFilter === "Approved") return "approved";
    if (statusFilter === "Denied") return "denied";
    return "all";
  }, [statusFilter]);

  const hasActiveFilters =
    statusFilter !== DEFAULT_STATUS_FILTER ||
    typeFilter !== "all" ||
    search.trim().length > 0;

  const sortedRequests = useMemo(
    () => sortAdminAccessRequests(requests, sortKey, sortDir),
    [requests, sortDir, sortKey],
  );

  const selectionPage = useMemo(() => {
    if (!selectedRequestId) {
      return null;
    }
    const index = sortedRequests.findIndex(
      (request) => request.id === selectedRequestId,
    );
    if (index < 0) {
      return null;
    }
    return Math.floor(index / ADMIN_REQUESTS_PAGE_SIZE) + 1;
  }, [selectedRequestId, sortedRequests]);

  // While a row is open, keep that row's page visible — unless the user just
  // paginated (preferExplicitPage), so the table does not wait on the close animation.
  const pageForView = preferExplicitPage ? page : (selectionPage ?? page);
  const { pageItems, pageCount, safePage } = useMemo(
    () => paginateAdminAccessRequests(sortedRequests, pageForView),
    [pageForView, sortedRequests],
  );

  const activeDetailRequest =
    (detailRequest?.id === selectedRequestId ? detailRequest : null) ??
    detailFromList;
  const activeDetailError =
    activeDetailRequest || !selectedRequestId
      ? null
      : detailError?.requestId === selectedRequestId
        ? detailError.message
        : null;

  const handleDetailUpdated = useCallback((updated: AdminAccessRequest) => {
    setDetailRequest(updated);
  }, []);

  useEffect(() => {
    if (!toastMessage) {
      return;
    }
    const handle = window.setTimeout(() => {
      setToastMessage(null);
    }, 3200);
    return () => window.clearTimeout(handle);
  }, [toastMessage]);

  const handleReviewed = useCallback((updated: AdminAccessRequest) => {
    setDetailRequest(updated);
    setRequests((prev) =>
      prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)),
    );
    setReloadKey((key) => key + 1);
    setToastMessage(
      updated.status === "approved"
        ? "Request approved."
        : "Request denied.",
    );
  }, []);

  const clearDrawerCloseTimeout = useCallback(() => {
    if (drawerCloseTimeoutRef.current !== null) {
      window.clearTimeout(drawerCloseTimeoutRef.current);
      drawerCloseTimeoutRef.current = null;
    }
  }, []);

  const commitClose = useCallback(() => {
    clearDrawerCloseTimeout();
    // Keep closingRequestId and preferExplicitPage until `request` leaves the
    // URL (cleared above when selectedRequestId becomes null). Clearing them
    // here races RR7 startTransition: drawer drops --closing and re-enters,
    // and pageForView can snap back to selectionPage.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("request");
      return next;
    });
    setDetailRequest(null);
    setDetailError(null);
  }, [clearDrawerCloseTimeout, setSearchParams]);

  const drawerClosing =
    closingRequestId !== null && closingRequestId === selectedRequestId;

  const requestClose = useCallback(() => {
    if (!selectedRequestId || drawerClosing) {
      return;
    }
    if (prefersReducedMotion()) {
      commitClose();
      return;
    }
    setClosingRequestId(selectedRequestId);
    drawerCloseTimeoutRef.current = window.setTimeout(() => {
      drawerCloseTimeoutRef.current = null;
      commitClose();
    }, DRAWER_CLOSE_MS);
  }, [commitClose, drawerClosing, selectedRequestId]);

  // Abort a pending close when the selection changes (URL or another row).
  useEffect(() => {
    if (closingRequestId === null || selectedRequestId === closingRequestId) {
      return;
    }
    clearDrawerCloseTimeout();
    const handle = window.setTimeout(() => {
      setClosingRequestId(null);
    }, 0);
    return () => window.clearTimeout(handle);
  }, [clearDrawerCloseTimeout, closingRequestId, selectedRequestId]);

  useEffect(() => {
    return () => {
      clearDrawerCloseTimeout();
    };
  }, [clearDrawerCloseTimeout]);

  // Remember the open row while the drawer is up, then restore focus after any
  // unmount path (Close, Escape, or URL dropping `request` without requestClose).
  // The focus trap skips its own restore (restoreFocus: false).
  // Also cancel a pending commitClose: render clears closingRequestId when
  // selectedRequestId is null, which skips the abort effect above, so the
  // timeout would otherwise survive and push another history entry / close a
  // drawer that was just reopened.
  useEffect(() => {
    if (selectedRequestId !== null) {
      drawerFocusRowIdRef.current = selectedRequestId;
      return;
    }
    clearDrawerCloseTimeout();
    const focusRowId = drawerFocusRowIdRef.current;
    if (!focusRowId) {
      return;
    }
    drawerFocusRowIdRef.current = null;
    const row = document.querySelector<HTMLElement>(
      `[data-testid="admin-request-row-${focusRowId}"]`,
    );
    row?.focus();
  }, [clearDrawerCloseTimeout, selectedRequestId]);

  const drawerOpen = Boolean(selectedRequestId);
  useDialogFocusTrap(drawerOpen, drawerRef, requestClose, drawerCloseRef, {
    restoreFocus: false,
  });

  function selectStatusFilter(key: StatusFilterKey) {
    setStatusFilter(statusFilterFromKey(key));
  }

  function clearFilters() {
    setStatusFilter(DEFAULT_STATUS_FILTER);
    setTypeFilter("all");
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!next.has("q")) {
          return prev;
        }
        next.delete("q");
        return next;
      },
      { replace: true },
    );
  }

  function openRequest(id: string) {
    // Cancel an in-progress close when opening another (or the same) request.
    clearDrawerCloseTimeout();
    setClosingRequestId(null);
    // Do not clear preferExplicitPage here — same startTransition race as
    // commitClose. Visible rows are on `page`, so the override stays valid.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("request", id);
      return next;
    });
  }

  function toggleRequest(id: string) {
    if (selectedRequestId === id) {
      // Same-row toggle while closing is a no-op (duplicate close ignored).
      requestClose();
      return;
    }
    openRequest(id);
  }

  function goToPage(nextPage: number) {
    const clamped = Math.min(Math.max(1, nextPage), pageCount);
    // Compare against the visible page (selectionPage ?? page), not raw `page`.
    // Deep links can leave `page` at 1 while the table shows page 2+.
    if (clamped === safePage) {
      return;
    }
    // Animate the drawer closed, but advance the table immediately.
    if (selectedRequestId) {
      setPreferExplicitPage(true);
      requestClose();
    }
    setPage(clamped);
  }

  function toggleSort(key: AdminRequestSortKey) {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "submitted" ? "desc" : "asc");
  }

  const listBusy = loadState.kind === "loading" || loadState.kind === "refreshing";
  const showRows =
    (loadState.kind === "ready" || loadState.kind === "refreshing") &&
    pageItems.length > 0;

  return (
    <AdminShell
      activeNav="control-panel"
      title="API access requests"
      titleTestId="admin-home-title"
      description="Start with requests in review. Open a row to inspect the intended use case and supporting details in the side panel."
      testId="admin-home"
      afterMain={
        <>
          {drawerOpen ? (
            <>
              <button
                type="button"
                className={`drawer-backdrop${drawerClosing ? " drawer-backdrop--closing" : ""}`}
                aria-label="Close request details"
                data-testid="admin-drawer-backdrop"
                onClick={requestClose}
              />
              <aside
                ref={drawerRef}
                className={`detail-drawer${drawerClosing ? " detail-drawer--closing" : ""}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="admin-drawer-title"
                data-testid="admin-request-drawer"
              >
                <div className="drawer-top">
                  <div className="drawer-heading">
                    <p className="drawer-kicker">Access request</p>
                    <h2 id="admin-drawer-title" data-testid="admin-drawer-title">
                      {activeDetailRequest?.contact_name ?? "Request details"}
                    </h2>
                    {activeDetailRequest ? (
                      <div
                        className="drawer-meta"
                        data-testid="admin-drawer-meta"
                      >
                        <StatusBadge status={activeDetailRequest.status} />
                        <TypePill type={activeDetailRequest.requester_type} />
                        <span className="drawer-submitted">
                          Submitted{" "}
                          {formatSubmittedDate(activeDetailRequest.created_at)}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <button
                    ref={drawerCloseRef}
                    type="button"
                    className="drawer-close"
                    data-testid="admin-drawer-close"
                    onClick={requestClose}
                  >
                    <i className="ti ti-x" aria-hidden="true" />
                    Close
                  </button>
                </div>
                <div className="drawer-body">
                  {activeDetailError ? (
                    <div className="empty-state" data-testid="admin-drawer-error">
                      <p>{activeDetailError}</p>
                      <Link
                        to="/admin/control-panel"
                        className="filter-btn on"
                        onClick={requestClose}
                      >
                        Back to list
                      </Link>
                    </div>
                  ) : null}
                  {!activeDetailError && !activeDetailRequest ? (
                    <div className="empty-state" data-testid="admin-drawer-loading">
                      <i className="ti ti-loader-2" aria-hidden="true" />
                      <p>Loading details…</p>
                    </div>
                  ) : null}
                  {activeDetailRequest ? (
                    <div data-testid={`admin-request-detail-${activeDetailRequest.id}`}>
                      <RequestDetailBody
                        request={activeDetailRequest}
                        onRequestUpdated={handleDetailUpdated}
                        onReviewed={handleReviewed}
                        onCopied={setToastMessage}
                      />
                    </div>
                  ) : null}
                </div>
              </aside>
            </>
          ) : null}

          {toastMessage ? (
            <div className="admin-toast" data-testid="admin-toast" role="status">
              {toastMessage}
            </div>
          ) : null}
        </>
      }
    >
          <div
            className="sum-row"
            role="group"
            aria-label="Filter by request status"
            data-testid="admin-summary"
          >
            <button
              type="button"
              className={`sum-card${activeSidebar === "all" ? " on" : ""}`}
              aria-pressed={activeSidebar === "all"}
              data-testid="admin-sum-all"
              onClick={() => selectStatusFilter("all")}
            >
              <div className="sum-lbl">Total</div>
              <div className="sum-val" data-testid="admin-count-total">
                {summary.total}
              </div>
              <div className="sum-sub">across all statuses</div>
            </button>
            <button
              type="button"
              className={`sum-card${activeSidebar === "review" ? " on" : ""}`}
              aria-pressed={activeSidebar === "review"}
              data-testid="admin-sum-review"
              onClick={() => selectStatusFilter("review")}
            >
              <div className="sum-lbl">In review</div>
              <div
                className="sum-val sum-val--review"
                data-testid="admin-count-review"
              >
                {summary.pending}
              </div>
              <div className="sum-sub">awaiting a decision</div>
            </button>
            <button
              type="button"
              className={`sum-card${activeSidebar === "approved" ? " on" : ""}`}
              aria-pressed={activeSidebar === "approved"}
              data-testid="admin-sum-approved"
              onClick={() => selectStatusFilter("approved")}
            >
              <div className="sum-lbl">Approved</div>
              <div
                className="sum-val sum-val--approved"
                data-testid="admin-count-approved"
              >
                {summary.approved}
              </div>
              <div className="sum-sub">access granted</div>
            </button>
            <button
              type="button"
              className={`sum-card${activeSidebar === "denied" ? " on" : ""}`}
              aria-pressed={activeSidebar === "denied"}
              data-testid="admin-sum-denied"
              onClick={() => selectStatusFilter("denied")}
            >
              <div className="sum-lbl">Denied</div>
              <div
                className="sum-val sum-val--denied"
                data-testid="admin-count-denied"
              >
                {summary.rejected}
              </div>
              <div className="sum-sub">access declined</div>
            </button>
          </div>

          <div
            className="mobile-status"
            role="group"
            aria-label="Status filters"
            data-testid="admin-mobile-status"
          >
            <button
              type="button"
              className={`mobile-status-btn${activeSidebar === "all" ? " on" : ""}`}
              aria-pressed={activeSidebar === "all"}
              data-testid="admin-mobile-filter-all"
              onClick={() => selectStatusFilter("all")}
            >
              All <span>{summary.total}</span>
            </button>
            <button
              type="button"
              className={`mobile-status-btn${activeSidebar === "review" ? " on" : ""}`}
              aria-pressed={activeSidebar === "review"}
              data-testid="admin-mobile-filter-review"
              onClick={() => selectStatusFilter("review")}
            >
              In review <span>{summary.pending}</span>
            </button>
            <button
              type="button"
              className={`mobile-status-btn${activeSidebar === "approved" ? " on" : ""}`}
              aria-pressed={activeSidebar === "approved"}
              data-testid="admin-mobile-filter-approved"
              onClick={() => selectStatusFilter("approved")}
            >
              Approved <span>{summary.approved}</span>
            </button>
            <button
              type="button"
              className={`mobile-status-btn${activeSidebar === "denied" ? " on" : ""}`}
              aria-pressed={activeSidebar === "denied"}
              data-testid="admin-mobile-filter-denied"
              onClick={() => selectStatusFilter("denied")}
            >
              Denied <span>{summary.rejected}</span>
            </button>
          </div>

          <div className="toolbar">
            <div className="search-wrap">
              <i className="ti ti-search" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name, email or business…"
                aria-label="Search requests"
                data-testid="admin-search"
              />
            </div>
            <button
              type="button"
              className={`filter-btn${typeFilter === "all" ? " on" : ""}`}
              aria-pressed={typeFilter === "all"}
              data-testid="admin-type-all"
              onClick={() => setTypeFilter("all")}
            >
              All types
            </button>
            <button
              type="button"
              className={`filter-btn${typeFilter === "customer" ? " on" : ""}`}
              aria-pressed={typeFilter === "customer"}
              data-testid="admin-type-customer"
              onClick={() => setTypeFilter("customer")}
            >
              <i className="ti ti-building" aria-hidden="true" /> Customer
            </button>
            <button
              type="button"
              className={`filter-btn${typeFilter === "partner" ? " on" : ""}`}
              aria-pressed={typeFilter === "partner"}
              data-testid="admin-type-partner"
              onClick={() => setTypeFilter("partner")}
            >
              <i className="ti ti-building-skyscraper" aria-hidden="true" />{" "}
              Partner
            </button>
            <button
              type="button"
              className={`filter-btn${loadState.kind === "refreshing" ? " is-busy" : ""}`}
              data-testid="admin-refresh"
              aria-label="Refresh access requests"
              disabled={listBusy}
              onClick={() => setReloadKey((key) => key + 1)}
            >
              <i
                className={`ti ${listBusy ? "ti-loader-2" : "ti-refresh"}`}
                aria-hidden="true"
              />
              {loadState.kind === "refreshing" ? "Updating" : "Refresh"}
            </button>
          </div>

          <div className="results-meta" data-testid="admin-results-meta">
            <span>
              {adminResultRangeLabel(sortedRequests.length, safePage)}
            </span>
            {hasActiveFilters ? (
              <button
                type="button"
                className="results-clear"
                data-testid="admin-clear-filters"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            ) : null}
          </div>

          <div className="tbl-wrap">
            <div
              className={`tbl-scroll${loadState.kind === "refreshing" ? " is-refreshing" : ""}`}
              data-testid="admin-request-list"
              role="region"
              tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex -- overflow lists must take keyboard focus
              aria-label="Access requests list"
              aria-busy={listBusy}
            >
              {loadState.kind === "refreshing" ? (
                <div
                  className="tbl-refresh"
                  data-testid="admin-refreshing"
                  role="status"
                  aria-live="polite"
                >
                  <i className="ti ti-loader-2" aria-hidden="true" />
                  <span>Updating results…</span>
                </div>
              ) : null}

              {loadState.kind === "loading" ? (
                <div className="empty-state" data-testid="admin-loading">
                  <i className="ti ti-loader-2" aria-hidden="true" />
                  <p>Loading requests…</p>
                </div>
              ) : null}

              {loadState.kind === "error" ? (
                <div className="empty-state" data-testid="admin-load-error">
                  <i className="ti ti-alert-circle" aria-hidden="true" />
                  <p>{loadState.message}</p>
                  <button
                    type="button"
                    className="filter-btn on"
                    onClick={() => setReloadKey((key) => key + 1)}
                  >
                    Retry
                  </button>
                </div>
              ) : null}

              {loadState.kind === "ready" && requests.length === 0 ? (
                <div className="empty-state" data-testid="admin-empty">
                  <i className="ti ti-inbox" aria-hidden="true" />
                  <p>No requests match the current filters.</p>
                </div>
              ) : null}

              {showRows ? (
                <table className="req-table">
                  <thead>
                    <tr>
                      {SORTABLE_COLUMNS.map((key) => {
                        const active = sortKey === key;
                        return (
                          <th
                            key={key}
                            scope="col"
                            data-testid={`admin-sort-${key}-header`}
                            aria-sort={
                              active
                                ? sortDir === "asc"
                                  ? "ascending"
                                  : "descending"
                                : "none"
                            }
                          >
                            <button
                              type="button"
                              className={`th-sort${active ? " on" : ""}`}
                              data-testid={`admin-sort-${key}`}
                              onClick={() => toggleSort(key)}
                            >
                              {adminSortColumnLabel(key)}
                              <i
                                className={`ti ${
                                  active
                                    ? sortDir === "asc"
                                      ? "ti-arrow-up"
                                      : "ti-arrow-down"
                                    : "ti-selector"
                                }`}
                                aria-hidden="true"
                              />
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((request) => {
                      const selected = selectedRequestId === request.id;
                      return (
                        <tr
                          key={request.id}
                          className={`req-tr req-tr--interactive${selected ? " on" : ""}`}
                          data-testid={`admin-request-row-${request.id}`}
                          tabIndex={0}
                          aria-selected={selected}
                          onClick={() => toggleRequest(request.id)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleRequest(request.id);
                            }
                          }}
                        >
                          <td>
                            <div
                              className="req-name"
                              data-testid="admin-list-contact-name"
                            >
                              {request.contact_name}
                            </div>
                            <div
                              className="req-email"
                              data-testid="admin-list-email"
                            >
                              {request.email}
                            </div>
                            {request.integrator_name?.trim() ? (
                              <div
                                className="req-integrator"
                                data-testid="admin-list-integrator-name"
                              >
                                {request.integrator_name}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <div className="req-biz">{request.company_name}</div>
                            <div className="req-use">
                              {previewIntendedUseCase(request.intended_use_case)}
                            </div>
                          </td>
                          <td>
                            <TypePill type={request.requester_type} />
                          </td>
                          <td>
                            <EnvironmentPill environment={request.environment} />
                          </td>
                          <td>
                            <div className="req-vol">
                              {volumeToLabel(request.expected_daily_volume)}
                            </div>
                          </td>
                          <td>
                            <StatusBadge status={request.status} />
                          </td>
                          <td>
                            <div className="req-date">
                              {formatSubmittedDate(request.created_at)}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : null}
            </div>

            {sortedRequests.length > ADMIN_REQUESTS_PAGE_SIZE ? (
              <div className="pager" data-testid="admin-pagination">
                <button
                  type="button"
                  className="filter-btn"
                  data-testid="admin-page-prev"
                  disabled={safePage <= 1}
                  onClick={() => goToPage(safePage - 1)}
                >
                  Previous
                </button>
                <span data-testid="admin-page-label">
                  Page {safePage} of {pageCount}
                </span>
                <button
                  type="button"
                  className="filter-btn"
                  data-testid="admin-page-next"
                  disabled={safePage >= pageCount}
                  onClick={() => goToPage(safePage + 1)}
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
    </AdminShell>
  );
}
